import { useEffect, useState } from 'react';
import { usePostMessage } from '../../context/messageContext';
import { useTestContext } from '../../context/testContext';
import { useCalibration } from '../../context/calibrationContext';
import { Button, BackButton, SessionActions, useSessionUI } from '../../ui';

// Visual Field nav-row controls (Figma session/12 left): [Back] [Stop Test] [Start Test]. When the
// test has ended, Stop becomes "Clear Results" and the primary becomes "End Test" / "Next Eye".
// Must stay mounted during the test: it stores the eye's results when the test ends (see effect).

const VisualFieldControls = ({ testProgressData, testIsRunning, setTestIsRunning, initialiseDotData, eyeTested, updateStep }) => {
  const [testConfig, setTestConfig] = useState({});
  const { updateTestData, testData } = useTestContext();
  const { confirm } = useSessionUI();
  const postMessage = usePostMessage();
  const testingAnotherEye = eyeTested === 'rightEye' && testData.testLeftEye;
  const {
    calibrationData: { distanceFromScreen },
  } = useCalibration();

  useEffect(() => {
    setTestConfig({
      contrastType: testData?.[eyeTested]?.['contrastType'],
      targetDuration: testData?.[eyeTested]?.['targetDuration'],
      fixationType: testData?.[eyeTested]?.['fixationType'],
    });
  }, [eyeTested]);

  useEffect(() => {
    if (!testProgressData.testEnded) return;
    return () =>
      updateTestData({
        visualFieldTwoResults: {
          ...(testData?.visualFieldTwoResults || {}),
          [eyeTested]: {
            ...testProgressData,
            ...testConfig,
            patientDistance: distanceFromScreen,
          },
        },
      });
  }, [testProgressData, testData?.visualFieldTwoResults, updateTestData, eyeTested, testConfig, distanceFromScreen]);

  const startTest = () => {
    postMessage({ type: 'startTest' });
    setTestIsRunning(true);
  };

  const stopTest = () => {
    setTestIsRunning(false);
    postMessage({ type: 'stopTest' });
    clearResults('noConfirmation');
  };

  const clearResults = async (noConfirmNeeded) => {
    if (noConfirmNeeded || (await confirm({ message: 'Are you sure you want to clear these results and begin the test again?', tone: 'danger' }))) {
      setTestIsRunning(false);
      postMessage({ type: 'useVisualFieldReducer', action: 'reset' });
      initialiseDotData();
    }
  };

  const finishTest = () => {
    const nextStep = testingAnotherEye ? 'leftEyeSetup' : 'results';
    updateStep(nextStep);
  };

  return (
    <SessionActions
      primary={
        testProgressData.testEnded
          ? { label: testingAnotherEye ? 'Next Eye' : 'End Test', onClick: () => finishTest() }
          : { label: 'Start Test', onClick: () => startTest(), disabled: testIsRunning }
      }
    >
      <BackButton onClick={() => updateStep(`${eyeTested}Setup`)} />
      <Button
        variant={testIsRunning ? 'destructive' : 'secondary'}
        size='xl'
        disabled={!testIsRunning}
        onClick={() => (testProgressData.testEnded ? clearResults() : stopTest())}
        data-testid='vf-stop'
      >
        {testProgressData.testEnded ? 'Clear Results' : 'Stop Test'}
      </Button>
    </SessionActions>
  );
};

export default VisualFieldControls;
